/**
 * Store for managing pre-approved member emails
 *
 * This store handles the functionality for captains to pre-approve emails
 * for joining their SubKrewes, creating a more welcoming experience for
 * expected members.
 */

import type { DocumentData } from 'firebase/firestore'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { PreapprovedMember } from '~/shared/types/preapprovedMember.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, liveQueryListener } from '~/utils/liveData'

const COLLECTION = 'preapproved_members'

function toPreapproval(id: string, data: DocumentData): PreapprovedMember {
  return {
    id,
    subkreweId: data.subkreweId,
    email: data.email,
    addedBy: data.addedBy,
    // A server timestamp while it's pending
    addedAt: data.addedAt ? (data.addedAt.toDate ? data.addedAt.toDate() : data.addedAt) : new Date(),
    used: data.used,
  }
}

export const usePreapprovedMembersStore = defineStore('preapprovedMembers', {
  state: () => ({
    preapprovedMembers: {} as Record<string, PreapprovedMember>,
    loaded: {} as Record<string, boolean>, // Track which SubKrewes we've loaded
  }),

  getters: {
    /**
     * Get pre-approved members for a specific SubKrewe
     */
    getPreapprovedForSubKrewe(): (subkreweId: string) => PreapprovedMember[] {
      return (subkreweId: string) => {
        return Object.values(this.preapprovedMembers).filter(
          member => member.subkreweId === subkreweId,
        )
      }
    },
  },

  actions: {
    /**
     * Check if an email is pre-approved for a specific SubKrewe
     */
    isEmailPreapproved(subkreweId: string, email: string): boolean {
      // Make sure we have loaded the data for this SubKrewe
      if (!this.loaded[subkreweId]) {
        console.warn('Pre-approved members not loaded for this SubKrewe')
        return false
      }

      // Normalize the email for comparison
      const normalizedEmail = email.trim().toLowerCase()

      // Check if there's a matching unused pre-approval
      return Object.values(this.preapprovedMembers).some(
        member =>
          member.subkreweId === subkreweId
          && member.email === normalizedEmail,
      )
    },

    /**
     * Add multiple pre-approved emails for a SubKrewe
     * @param subkreweId The SubKrewe ID
     * @param emailsText Text with one email per line
     * @returns Number of emails added (0 when all were already listed), or
     *   null when they couldn't be added
     */
    async addBulkPreapprovedMembers(subkreweId: string, emailsText: string): Promise<number | null> {
      try {
        if (!subkreweId || !emailsText.trim()) {
          return 0
        }

        const db = useFirestore()
        const currentUser = useCurrentUser()

        if (!currentUser.value?.uid) {
          console.error('User not authenticated')
          return null
        }

        // Store the authenticated user to help TypeScript understand it's safe to use
        const authenticatedUser = currentUser.value

        // Parse emails (one per line), normalize, and remove duplicates
        const rawEmails = emailsText.split(/[\n,]/)
          .map(email => email.trim().toLowerCase())
          .filter(email => email.length > 0 && email.includes('@'))

        // Remove duplicates
        const uniqueEmails = [...new Set(rawEmails)]

        // Create a batch of promises to add each email
        const promises = uniqueEmails.map(async (email) => {
          // Don't add if this email is already pre-approved for this SubKrewe
          const existing = Object.values(this.preapprovedMembers).find(
            m => m.subkreweId === subkreweId && m.email === email,
          )

          if (existing) {
            return null // Skip this one
          }

          // Add the new pre-approval
          const preapprovalData = {
            subkreweId,
            email,
            addedBy: authenticatedUser.uid,
            addedAt: serverTimestamp(),
            used: false,
          }

          const docRef = await addDoc(collection(db, 'preapproved_members'), preapprovalData)

          // Add to local state
          const newPreapproval: PreapprovedMember = {
            id: docRef.id,
            ...preapprovalData,
            addedAt: new Date(),
          }

          this.preapprovedMembers[docRef.id] = newPreapproval
          return newPreapproval
        })

        // Wait for all operations to complete
        const results = await Promise.all(promises)

        // Mark this SubKrewe as loaded
        this.loaded[subkreweId] = true

        // Return count of successfully added emails
        return results.filter(r => r !== null).length
      }
      catch (error) {
        console.error('Error adding pre-approved members:', error)
        return null
      }
    },

    /**
     * A SubKrewe's pre-approvals, live while the caller holds them
     * (utils/liveData.ts)
     */
    async loadForSubKrewe(subkreweId: string): Promise<void> {
      try {
        await acquireLive(this.subkreweSpec(subkreweId))
      }
      catch (error) {
        console.error(`Error loading pre-approved members for SubKrewe ${subkreweId}:`, error)
      }
    },

    subkreweSpec(subkreweId: string): LiveSpec<DocumentData> {
      return {
        key: `${COLLECTION}?subkreweId=${subkreweId}`,
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), COLLECTION), where('subkreweId', '==', subkreweId)),
          ...liveQueryListener(COLLECTION, emit, fail),
        ),
        apply: changes => this.applyPreapprovals(changes),
        evict: paths => this.evictPreapprovals(paths),
        ready: () => {
          this.$patch((state) => {
            state.loaded[subkreweId] = true
          })
        },
        stopped: () => {
          this.$patch((state) => {
            delete state.loaded[subkreweId]
          })
        },
      }
    },

    emailSpec(email: string): LiveSpec<DocumentData> {
      return {
        key: `${COLLECTION}?email=${email}&used=false`,
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), COLLECTION), where('email', '==', email), where('used', '==', false)),
          ...liveQueryListener(COLLECTION, emit, fail),
        ),
        apply: changes => this.applyPreapprovals(changes),
        evict: paths => this.evictPreapprovals(paths),
      }
    },

    applyPreapprovals(changes: LiveChange<DocumentData>[]) {
      this.$patch((state) => {
        for (const { path, data } of changes) {
          const id = path.slice(COLLECTION.length + 1)
          state.preapprovedMembers[id] = toPreapproval(id, data!)
        }
      })
    },

    evictPreapprovals(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.preapprovedMembers[path.slice(COLLECTION.length + 1)]
      })
    },

    /**
     * Delete a pre-approval
     */
    async deletePreapproval(preapprovalId: string): Promise<boolean> {
      try {
        const db = useFirestore()

        // Delete from Firestore
        const preapprovalRef = doc(db, 'preapproved_members', preapprovalId)
        await deleteDoc(preapprovalRef)

        // Remove from state
        if (this.preapprovedMembers[preapprovalId]) {
          const { subkreweId } = this.preapprovedMembers[preapprovalId]
          delete this.preapprovedMembers[preapprovalId]

          // Make sure we keep track that we've loaded this SubKrewe
          this.loaded[subkreweId] = true
        }

        return true
      }
      catch (error) {
        console.error(`Error deleting pre-approval ${preapprovalId}:`, error)
        return false
      }
    },

    /**
     * The unused pre-approvals for an email, live while the caller holds
     * them. The sign-up flow uses this to direct users to their SubKrewes.
     */
    async findPreapprovalsByEmail(email: string): Promise<PreapprovedMember[]> {
      const normalizedEmail = email.trim().toLowerCase()
      try {
        await acquireLive(this.emailSpec(normalizedEmail))
      }
      catch (error) {
        console.error(`Error finding pre-approvals for email ${email}:`, error)
        return []
      }
      return Object.values(this.preapprovedMembers).filter(
        preapproval => preapproval.email === normalizedEmail && preapproval.used === false,
      )
    },
  },
})
